import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDeal } from '../deals/DealContext.jsx';
import { dealDataUrl } from '../deals/registry.js';
import { fetchDeskJson } from '../lib/deskDataFetch.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { screenApi, waitForScreen } from '../screen/api.js';
import { OUTCOME_LABEL, fmtServerValue, mergeServerScreen, screenDeal } from './model.js';
import { useAssistantSummary } from './common.jsx';
import { fetchWorkflow } from '../assets/api.js';
import { checklistForAssistant, computeChecklist } from '../diligence/checklist.js';
import { fetchOverrides } from '../diligence/api.js';
import ReadinessChip from '../diligence/ReadinessChip.jsx';

/** Client fallback: screen a deal from its served JSON when the server has no result yet. */
async function loadClient(deal, refreshSession) {
  const get = (name) =>
    fetchDeskJson(dealDataUrl(deal, name, import.meta.env.BASE_URL), { refreshSession }).catch(() => null);
  const [pkg, diligence, executive, wells, land, map] = await Promise.all([
    get('deal-package.json'),
    get('diligence.json'),
    get('executive.json'),
    get('wells.json'),
    get('land.json'),
    get('map.json'),
  ]);
  let wf = null;
  try {
    wf = await fetchWorkflow(deal.dealId);
  } catch {
    wf = null;
  }
  let overrides = [];
  let settings = { stage: 'pre_offer', closingDate: null };
  try {
    const ov = await fetchOverrides(deal.dealId);
    overrides = ov?.overrides || [];
    settings = { stage: ov?.settings?.stage || ov?.stage || 'pre_offer', closingDate: ov?.settings?.closingDate || null };
  } catch {
    overrides = [];
  }
  return { pkg, diligence, executive, wells, land, map, wf, overrides, settings };
}

const ORDER = { outside: 0, review: 1, inside: 2, insufficient: 3 };
const COLS = [
  { key: 'pdpPv10', label: 'Seller PDP PV10' },
  { key: 'top1Share', label: 'Largest well' },
  { key: 'aroPctPdpPv', label: 'ARO / PV10' },
  { key: 'pvWeightedLife', label: 'Reserve life' },
  { key: 'l12Ncf', label: 'L12M net CF' },
  { key: 'absNote', label: 'ABS note, package deck' },
];
const STATUS_CLASS = { outside: 'bad', review: 'dim', nodata: 'dim' };

export default function DealsHome({ onOpenDeal, onOpenScreen }) {
  const auth = useAuth();
  const { deals } = useDeal();
  const [server, setServer] = useState({});
  const [client, setClient] = useState({});
  const [running, setRunning] = useState({});
  const [err, setErr] = useState(null);
  const refreshSession = useCallback(() => auth.refresh(), [auth]);

  const refresh = useCallback(async () => {
    try {
      const res = await screenApi.deals();
      setServer(Object.fromEntries((res.deals || []).map((d) => [d.dealId, d])));
    } catch (e) {
      setErr(e.status === 404 ? null : e.message);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Client inputs load for every deal: the screen falls back to them when the
  // server has not screened a deal, and the checklist always needs them.
  const ids = deals.map((d) => d.dealId).join('|');
  useEffect(() => {
    let cancelled = false;
    deals.forEach((d) => {
      if (client[d.dealId]) return;
      loadClient(d, refreshSession).then((res) => {
        if (!cancelled) setClient((prev) => ({ ...prev, [d.dealId]: res }));
      });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, server]);

  const serverRunning = Object.values(server).some((d) => d.running);
  useEffect(() => {
    if (!serverRunning) return undefined;
    const t = setTimeout(refresh, 3000);
    return () => clearTimeout(t);
  }, [serverRunning, server, refresh]);

  const runAll = useCallback(async () => {
    setErr(null);
    await Promise.all(
      deals.map(async (d) => {
        setRunning((m) => ({ ...m, [d.dealId]: true }));
        try {
          await waitForScreen(d.dealId, { start: true });
        } catch (e) {
          setErr(`${d.label}: ${e.message}`);
        } finally {
          setRunning((m) => ({ ...m, [d.dealId]: false }));
        }
      }),
    );
    refresh();
  }, [deals, refresh]);

  const rows = useMemo(
    () =>
      deals.map((d) => {
        const sv = server[d.dealId];
        const cl = client[d.dealId];
        const result = sv?.result || null;
        const clientScreen = cl?.pkg
          ? screenDeal(cl.pkg, { diligence: cl.diligence, executive: cl.executive })
          : null;
        const merged = result ? mergeServerScreen(clientScreen || {}, result) : clientScreen;
        const byKey = Object.fromEntries((result?.criteria || []).map((c) => [c.key, c]));
        const clientCell = (k) => {
          if (!clientScreen) return null;
          const f = clientScreen.facts;
          const conc = clientScreen.checks.find((c) => c.id === 'concentration');
          if (k === 'pdpPv10') return { text: fmtServerValue(f.refPdpPv10MM, '$MM') };
          if (k === 'top1Share') return { text: conc?.value, status: conc?.status === 'fail' ? 'outside' : null };
          if (k === 'pvWeightedLife') return { text: fmtServerValue(f.pvWeightedLife, 'yrs') };
          if (k === 'l12Ncf') return { text: fmtServerValue(f.l12Nocf, '$MM') };
          return { text: '—' };
        };
        const cell = (k) =>
          byKey[k] ? { text: fmtServerValue(byKey[k].value, byKey[k].unit), status: byKey[k].status } : clientCell(k);
        const hasChecklistInputs = Boolean(cl?.pkg || cl?.diligence || cl?.wf || cl?.wells);
        const checklist = hasChecklistInputs
          ? computeChecklist({
              pkg: cl.pkg,
              diligence: cl.diligence,
              wf: cl.wf,
              wells: cl.wells,
              land: cl.land,
              map: cl.map,
              deal: d,
              overrides: cl.overrides,
              settings: cl.settings,
            })
          : null;
        return {
          deal: d,
          checklist,
          teaser: sv?.kind === 'teaser' || d.kind === 'teaser',
          outcome: merged?.outcome || (cl ? 'insufficient' : null),
          busy: running[d.dealId] || sv?.running,
          stale: sv?.stale,
          source: result ? 'server' : clientScreen ? 'client' : null,
          topRisk: result?.risks?.[0]?.title || cl?.diligence?.risks?.[0]?.title || null,
          cell,
        };
      }),
    [deals, server, client, running],
  );
  const sorted = [...rows].sort((a, b) => (ORDER[a.outcome ?? 'insufficient'] ?? 9) - (ORDER[b.outcome ?? 'insufficient'] ?? 9));
  const counts = rows.reduce((acc, r) => {
    const k = r.outcome || 'loading';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const anyRunning = Object.values(running).some(Boolean) || serverRunning;

  useAssistantSummary({
    surface: 'deals',
    deals: rows.map((r) => ({
      deal: r.deal.label,
      outcome: r.outcome ? OUTCOME_LABEL[r.outcome] : 'not screened',
      checklist: checklistForAssistant(r.checklist),
    })),
  });

  return (
    <div className="mp mp-deals" style={{ maxWidth: 1320 }}>
      <div className="mp-head">
        <div>
          <span className="mp-eyebrow">
            {auth.user?.name ? `${auth.user.name.split(' ')[0]}, ` : ''}every deal on the desk, screened the same way
          </span>
          <h2 className="mp-h2">Deals</h2>
        </div>
        <div style={{ display: 'flex', flexDirection: 'row', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {['outside', 'review', 'inside', 'insufficient'].map((k) =>
            counts[k] ? (
              <span key={k} className={`mp-badge ${k}`}>
                {counts[k]} {OUTCOME_LABEL[k].toLowerCase()}
              </span>
            ) : null,
          )}
          <button type="button" className="mp-btn amber small" disabled={anyRunning} onClick={runAll}>
            {anyRunning ? 'Screening…' : 'Re-run screen'}
          </button>
        </div>
      </div>

      {err ? <div className="mp-note" role="status" style={{ color: 'var(--mp-amber-ink)' }}>{err}</div> : null}

      <section className="mp-card" aria-label="All deals">
        <div className="mp-table-wrap">
          <table className="mp-table mp-deals-table" style={{ minWidth: 960 }}>
            <thead>
              <tr>
                <th>Deal</th>
                <th>Screen</th>
                <th style={{ whiteSpace: 'nowrap' }} title="Pre-offer data in hand: required checklist items, Complete 1, Partial ½">
                  Data in hand
                </th>
                {COLS.map((c) => (
                  <th key={c.key} className="num">
                    {c.label}
                  </th>
                ))}
                <th>Top risk</th>
                <th className="mp-deals-open" aria-label="Open" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr
                  key={r.deal.dealId}
                  className={r.teaser ? '' : 'clickable'}
                  onClick={r.teaser ? undefined : () => onOpenDeal(r.deal.dealId)}
                >
                  <td>
                    <div className="mp-dealname">
                      <span className="mp-mark">{r.deal.mark || r.deal.shortLabel?.slice(0, 3).toUpperCase()}</span>
                      <span>
                        <b>{r.deal.label}</b>
                        <small>{r.deal.subtitle}</small>
                      </span>
                    </div>
                  </td>
                  <td>
                    {r.busy ? (
                      <span className="mp-note">Screening…</span>
                    ) : r.outcome ? (
                      <>
                        <span className={`mp-badge ${r.outcome}`}>{OUTCOME_LABEL[r.outcome]}</span>
                        {r.stale ? <div className="mp-note">inputs changed</div> : null}
                      </>
                    ) : (
                      <span className="mp-note">Loading…</span>
                    )}
                  </td>
                  <td>
                    <ReadinessChip
                      pct={r.checklist?.readiness?.pre_offer?.pct}
                      complete={r.checklist?.readiness?.pre_offer?.complete}
                      partial={r.checklist?.readiness?.pre_offer?.partial}
                      required={r.checklist?.readiness?.pre_offer?.required}
                      overdue={r.checklist?.warnings?.overdue}
                      compact
                      testId={`readiness-${r.deal.dealId}`}
                    />
                  </td>
                  {COLS.map((c) => {
                    const v = r.cell(c.key);
                    return (
                      <td key={c.key} className={`num ${STATUS_CLASS[v?.status] || ''}`}>
                        {v?.text || '—'}
                      </td>
                    );
                  })}
                  <td className="mp-deals-risk">
                    {r.topRisk || (r.outcome === 'insufficient' ? <span className="mp-note">No package loaded</span> : '—')}
                  </td>
                  <td className="mp-deals-open">
                    {r.teaser ? (
                      <span className="mp-note">teaser</span>
                    ) : (
                      <button
                        type="button"
                        className="mp-btn small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenDeal(r.deal.dealId);
                        }}
                      >
                        Open
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <span className="mp-note">
            Screens are labels, not recommendations. ARO is the configured D2D estimate unless overridden. ABS note size, package deck (unscaled 1PDP). Finance sizes the base strip.
          </span>
          {onOpenScreen ? (
            <button type="button" className="mp-link" onClick={onOpenScreen}>
              Screen details &amp; criteria →
            </button>
          ) : null}
        </div>
      </section>
    </div>
  );
}
